import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  forwardRef,
} from 'react';
import {
  Autocomplete,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  Tooltip,
  Typography,
  Box,
} from '@mui/material';
import type { FilterOptionsState } from '@mui/material';

import type { UUID, CustomStandardBase, StoredStandard } from '@/shared/types/customStandard';
import type { UserStandard } from '@/shared/types/userStandard';
import { useGetAllByOwnerQuery } from '@/features/customStandards/customStandardsApi';
import { useGetAllUserStandardsQuery } from '@/features/userStandards/standardsCatalogApi';
import { isFeatureEnabled } from '@/shared/libs/featureFlags';

type StandardSourceRow = {
  id: UUID;
  parent_id: UUID | null;
  title: string;
  code: string | null;
  position: number;
  node_type?: 'folder' | 'standard';
  standards_warehouse_id?: number | null;
  csp_standard_id?: UUID | null;
  description?: string | null;
  additional_content?: Record<string, unknown> | null;
};

function additionalString(row: StandardSourceRow, key: string) {
  const value = row.additional_content?.[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

function sourceFromRow(row: StandardSourceRow) {
  return additionalString(row, 'sourceLabel') ?? additionalString(row, 'standardSetTitle');
}

/* ------------------------------------------------------------------ */
/*  Scoped-footer context & Paper component (stable reference)        */
/* ------------------------------------------------------------------ */

type ScopedPaperCtx = {
  showFooter: boolean;
  showAll: boolean;
  toggle: () => void;
  scopeText: string;
};

const ScopedPaperContext = createContext<ScopedPaperCtx>({
  showFooter: false,
  showAll: false,
  toggle: () => {},
  scopeText: '',
});

const ScopedPaper = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
  function ScopedPaper(props, ref) {
    const { showFooter, showAll, toggle, scopeText } = useContext(ScopedPaperContext);
    return (
      <Paper {...props} ref={ref}>
        {props.children}
        {showFooter && (
          <Box
            onMouseDown={(e) => e.preventDefault()}
            onClick={toggle}
            sx={{
              py: 0.75,
              px: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              cursor: 'pointer',
              textAlign: 'center',
              '&:hover': { bgcolor: 'action.hover' },
              position: 'sticky',
              bottom: 0,
              bgcolor: 'background.paper',
              zIndex: 1,
            }}
          >
            <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
              {showAll ? `← Show only ${scopeText} standards` : 'Show all standards →'}
            </Typography>
          </Box>
        )}
      </Paper>
    );
  }
);

/* ------------------------------------------------------------------ */
/*  StandardsSelector                                                 */
/* ------------------------------------------------------------------ */

export type StandardsSelectorProps = {
  /** Currently selected standards — full inline snapshots, not bare IDs */
  value?: StoredStandard[];
  onChange: (standards: StoredStandard[]) => void;
  /** Scoped options from the parent entity's stored standards. When omitted, goes straight to "show all" mode. */
  parentScope?: StoredStandard[];
  /** Required only for "show all" mode — the current viewer's user ID */
  ownerId?: UUID;
  label?: string;
  disabled?: boolean;
  helperText?: string;
  allowAllToggle?: boolean;
  /** Label for the scope context, e.g. "course", "unit", "rubric" */
  scopeLabel?: string;
};

export const StandardsSelector: React.FC<StandardsSelectorProps> = ({
  value = [],
  onChange,
  parentScope,
  ownerId,
  label = 'Standards',
  disabled,
  helperText,
  allowAllToggle = true,
  scopeLabel,
}) => {
  const [showAll, setShowAll] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const toggleShowAll = useCallback(() => setShowAll((v) => !v), []);
  const useNewStandards = isFeatureEnabled('standardsLibraryV2');

  // Load viewer's full library lazily — only when in "show all" mode or when there is no parentScope
  const effectiveShowAll = !parentScope || showAll;
  const { data: legacyStandards = [], isLoading: legacyLoading } = useGetAllByOwnerQuery(
    { ownerId: ownerId! },
    { skip: !ownerId || useNewStandards || (!!parentScope && !showAll) }
  );
  const { data: newStandards = [], isLoading: newLoading } = useGetAllUserStandardsQuery(
    { ownerId: ownerId! },
    { skip: !ownerId || !useNewStandards || (!!parentScope && !showAll) }
  );
  const allStandards: StandardSourceRow[] = useNewStandards ? newStandards : legacyStandards;
  const isLoading = useNewStandards ? newLoading : legacyLoading;

  /* ── Hierarchy maps — populated only when allStandards is loaded ── */

  const allStandardsById = useMemo(
    () => new Map(allStandards.map((s) => [s.id, s])),
    [allStandards]
  );

  const rootCategoryIds = useMemo(() => {
    const hasChild = new Set<UUID>();
    for (const s of allStandards) {
      if (s.parent_id) hasChild.add(s.parent_id);
    }
    const set = new Set<UUID>();
    for (const s of allStandards) {
      if (s.parent_id === null && hasChild.has(s.id)) set.add(s.id);
    }
    return set;
  }, [allStandards]);

  const depthOf = useMemo(() => {
    const cache = new Map<UUID, number>();
    const compute = (id: UUID): number => {
      if (cache.has(id)) return cache.get(id)!;
      const s = allStandardsById.get(id);
      if (!s || !s.parent_id) {
        cache.set(id, 0);
        return 0;
      }
      const d = compute(s.parent_id) + 1;
      cache.set(id, d);
      return d;
    };
    for (const s of allStandards) compute(s.id);
    return cache;
  }, [allStandards, allStandardsById]);

  const globalSortOrder = useMemo(() => {
    const order = new Map<UUID, number>();
    const childrenOf = new Map<string, StandardSourceRow[]>();
    for (const s of allStandards) {
      const key = s.parent_id ?? 'root';
      if (!childrenOf.has(key)) childrenOf.set(key, []);
      childrenOf.get(key)!.push(s);
    }
    for (const kids of childrenOf.values()) kids.sort((a, b) => a.position - b.position);
    let idx = 0;
    const dfs = (pid: string) => {
      for (const c of childrenOf.get(pid) ?? []) {
        order.set(c.id, idx++);
        dfs(c.id);
      }
    };
    dfs('root');
    return order;
  }, [allStandards]);

  /* ── Helpers ── */

  const renderLabel = useCallback(
    (s: StoredStandard) => (s.code ? `${s.code} — ${s.title}` : s.title),
    []
  );

  const getRootAncestor = useCallback(
    (id: string): StandardSourceRow | undefined => {
      let cur = allStandardsById.get(id);
      while (cur?.parent_id) {
        const p = allStandardsById.get(cur.parent_id);
        if (!p) break;
        cur = p;
      }
      return cur;
    },
    [allStandardsById]
  );

  const getBreadcrumb = useCallback(
    (id: string): string => {
      const s = allStandardsById.get(id);
      if (!s) return '';
      const parts: string[] = [];
      let cur = s.parent_id ? allStandardsById.get(s.parent_id) : undefined;
      while (cur) {
        parts.unshift(cur.code || cur.title);
        cur = cur.parent_id ? allStandardsById.get(cur.parent_id) : undefined;
      }
      return parts.join(' › ');
    },
    [allStandardsById]
  );

  const getGroupLabel = useCallback(
    (option: StoredStandard): string => {
      const full = allStandardsById.get(option.id);
      if (!full || !full.parent_id) return 'Standards';
      const root = getRootAncestor(option.id);
      return root ? (root.code ? `${root.code} — ${root.title}` : root.title) : 'Standards';
    },
    [allStandardsById, getRootAncestor]
  );

  /* ── Options ── */

  const sortedOptions = useMemo<StoredStandard[]>(() => {
    if (effectiveShowAll) {
      const isSelectable = (s: StandardSourceRow) =>
        useNewStandards ? s.node_type === 'standard' : !rootCategoryIds.has(s.id);

      const options: StoredStandard[] = allStandards.filter(isSelectable).map((s) => ({
        id: s.id,
        code: s.code,
        title: s.title,
        description: s.description ?? null,
        source: useNewStandards ? sourceFromRow(s) : null,
        ...(useNewStandards
          ? { cspStandardId: (s as UserStandard).csp_standard_id ?? undefined }
          : { warehouseId: (s as CustomStandardBase).standards_warehouse_id ?? undefined }),
      }));
      return options.sort(
        (a, b) => (globalSortOrder.get(a.id) ?? 0) - (globalSortOrder.get(b.id) ?? 0)
      );
    }
    return parentScope!;
  }, [
    effectiveShowAll,
    allStandards,
    useNewStandards,
    rootCategoryIds,
    globalSortOrder,
    parentScope,
  ]);

  /* ── Filter ── */

  const filterOptions = useCallback(
    (options: StoredStandard[], state: FilterOptionsState<StoredStandard>) => {
      const selectedIds = new Set(value.map((v) => v.id));
      let filtered = options.filter((o) => !selectedIds.has(o.id));
      const q = state.inputValue.trim().toLowerCase();
      if (q) {
        filtered = filtered.filter((o) => {
          const searchableText = `${renderLabel(o)} ${getBreadcrumb(o.id)}`.toLowerCase();
          return searchableText.includes(q);
        });
      }
      return filtered;
    },
    [getBreadcrumb, renderLabel, value]
  );

  /* ── Scoped paper context ── */

  const scopeText = scopeLabel ? `this ${scopeLabel}'s` : 'scoped';
  const showFooter = !!parentScope && allowAllToggle;
  const paperCtx = useMemo<ScopedPaperCtx>(
    () => ({ showFooter, showAll, toggle: toggleShowAll, scopeText }),
    [showFooter, showAll, toggleShowAll, scopeText]
  );

  /* ── No-options text ── */

  const noOptionsText = useMemo(() => {
    if (parentScope && !showAll) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          No matches in {scopeText} standards
        </Typography>
      );
    }
    return 'No standards found';
  }, [parentScope, showAll, scopeText]);

  /* ── Render ── */

  return (
    <ScopedPaperContext.Provider value={paperCtx}>
      <Autocomplete<StoredStandard, true>
        multiple
        options={sortedOptions}
        groupBy={effectiveShowAll ? getGroupLabel : undefined}
        value={value}
        getOptionLabel={renderLabel}
        filterOptions={filterOptions}
        disableCloseOnSelect
        loading={isLoading}
        disabled={disabled}
        noOptionsText={noOptionsText}
        onChange={(_e, newVal) => onChange(newVal)}
        inputValue={inputValue}
        onInputChange={(_e, v) => setInputValue(v)}
        limitTags={3}
        slots={{ paper: ScopedPaper }}
        renderTags={(selected, getTagProps) =>
          selected.map((option, index) => (
            <Tooltip key={option.id} title={renderLabel(option)} arrow>
              <Chip
                {...getTagProps({ index })}
                label={renderLabel(option)}
                size="small"
                sx={{
                  maxWidth: '100%',
                  height: 'auto',
                  '& .MuiChip-label': {
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'normal',
                    lineHeight: 1.25,
                    overflowWrap: 'anywhere',
                    paddingTop: '6px',
                    paddingBottom: '6px',
                  },
                }}
              />
            </Tooltip>
          ))
        }
        renderOption={(props, option) => {
          const { key, ...restProps } = props;
          const depth = effectiveShowAll ? Math.max((depthOf.get(option.id) ?? 1) - 1, 0) : 0;
          const breadcrumb = effectiveShowAll ? getBreadcrumb(option.id) : '';

          return (
            <li
              key={key}
              {...restProps}
              style={{
                ...restProps.style,
                paddingLeft: `${16 + depth * 24}px`,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                  {renderLabel(option)}
                </span>
                {breadcrumb && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {breadcrumb}
                  </Typography>
                )}
              </Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={isLoading ? 'Loading...' : 'Search and select standards'}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
      />
    </ScopedPaperContext.Provider>
  );
};

export default StandardsSelector;
